import { saveAs } from "file-saver";
import type { VideoItemType, PhotoItemType, FilterMediaByType } from "./types";
import { MediaEnum } from "./enums";
import { isEmpty, orderBy, uniqBy, intersectionBy } from "lodash";
import { isVideoItem } from "./helpers";

export function getPhotoSwipeCustomButtons(
  filteredItems: (VideoItemType | PhotoItemType)[],
) {
  return [
    // {
    //   name: 'info-button',
    //   className: "",
    //   order: 8,
    //   isButton: true,
    //   html: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="text-white mx-auto scale-[1.3]"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/><path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/></svg>',
    //   onClick: (e: any, el: any, pswpInstance: any) => {
    //     console.log('e', e);
    //     console.log('el', el);
    //     console.log('pswpInstance', pswpInstance);
    //   },
    // },
    {
      name: "download-button",
      className: "",
      order: 9,
      isButton: true,
      html: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="text-white mx-auto scale-[1.3]">  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5"/><path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z"/></svg>',
      onClick: (e: any, el: any, pswpInstance: any) => {
        const index = pswpInstance.currIndex;
        const { url, name } = filteredItems[index];

        if (url) {
          saveAs(url, name);
        }
      },
    },
    {
      name: "close-button",
      className: "",
      order: 20,
      isButton: true,
      html: '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="text-white mx-auto scale-[2]"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/></svg>',
      onClick: (e: any, el: any, pswpInstance: any) => pswpInstance.close(),
    },
    // Zoom button: temp1.currSlide.zoomTo(1, null, pswpInstance.options.zoomAnimationDuration)
  ];
}

const orderFilteredPhotosPlusVideos = (
  filteredPhotoList: PhotoItemType[],
  videos: VideoItemType[],
) => {
  const orderedList = orderBy(
    [...filteredPhotoList, ...videos],
    ["date"],
    ["desc"],
  );
  return orderedList;
};

export function filterItems(
  items: (VideoItemType | PhotoItemType)[],
  filterBy: FilterMediaByType,
  showFilter = true,
  activeBackgroundFilterItems: string[] = [],
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  showPrivacyFilter: boolean = false,
  privacyFilterItems: boolean[] = [],
) {
  if (!showFilter) {
    return items;
  }

  const mediaTypeFilteredList = items?.filter(
    (item: VideoItemType | PhotoItemType) => filterBy[item.mediaType],
  );
  if (isEmpty(activeBackgroundFilterItems) && !showPrivacyFilter) {
    return mediaTypeFilteredList;
  }

  const photos = items.filter(
    (item) => item.mediaType === MediaEnum.Photo,
  ) as PhotoItemType[];
  const videos = items.filter(
    (item) => item.mediaType === MediaEnum.Video,
  ) as VideoItemType[];

  let filteredPrivacyPhotos: PhotoItemType[] = [];
  if (showPrivacyFilter && filterBy.photo) {
    let filteredPrivacyOpenedPhotos: PhotoItemType[] = [];
    let filteredPrivacyAcceptedPhotos: PhotoItemType[] = [];
    let filteredPrivacyRejectedPhotos: PhotoItemType[] = [];
    let filteredPrivacyDownloadedPhotos: PhotoItemType[] = [];
    if (privacyFilterItems[0]) {
      filteredPrivacyOpenedPhotos = photos.filter(
        (photo) => photo.userConsent?.opened === privacyFilterItems[0],
      );
    }
    if (privacyFilterItems[1]) {
      filteredPrivacyAcceptedPhotos = photos.filter(
        (photo) => photo.userConsent?.accepted === privacyFilterItems[1],
      );
    }
    if (privacyFilterItems[2]) {
      filteredPrivacyRejectedPhotos = photos.filter(
        (photo) => photo.userConsent?.rejected === privacyFilterItems[2],
      );
    }
    if (privacyFilterItems[3]) {
      filteredPrivacyDownloadedPhotos = photos.filter(
        (photo) => photo.userConsent?.downloaded === privacyFilterItems[3],
      );
    }

    filteredPrivacyPhotos = uniqBy(
      [
        ...filteredPrivacyOpenedPhotos,
        ...filteredPrivacyAcceptedPhotos,
        ...filteredPrivacyRejectedPhotos,
        ...filteredPrivacyDownloadedPhotos,
      ],
      "id",
    );
    if (filterBy.video && isEmpty(activeBackgroundFilterItems)) {
      return orderFilteredPhotosPlusVideos(filteredPrivacyPhotos, videos);
    }
  }

  let filteredBackgroundPhotos: PhotoItemType[] = [];
  if (!isEmpty(activeBackgroundFilterItems) && filterBy.photo) {
    filteredBackgroundPhotos = photos.filter((photo) => {
      let backgroundFileNameMatch = false;
      activeBackgroundFilterItems.forEach((activeBackgroundFilterItem) => {
      if (
        activeBackgroundFilterItem === null
          ? photo.backgroundFileName === null
          : photo. backgroundFileName?. startsWith(activeBackgroundFilterItem)
      ) {
        backgroundFileNameMatch = true;
      }
      });
      return backgroundFileNameMatch;
    });
    if (showPrivacyFilter) {
      return intersectionBy(
        filteredPrivacyPhotos,
        filteredBackgroundPhotos,
        "id",
      );
    }
    if (filterBy.video && !showPrivacyFilter) {
      return orderFilteredPhotosPlusVideos(filteredBackgroundPhotos, videos);
    }
  }

  if (filterBy.photo && filterBy.video) {
    return orderFilteredPhotosPlusVideos(
      uniqBy([...filteredPrivacyPhotos, ...filteredBackgroundPhotos], "id"),
      videos,
    );
  } else {
    return uniqBy(
      [...filteredPrivacyPhotos, ...filteredBackgroundPhotos],
      "id",
    );
  }
}

export function isItemUnProcessed(item: VideoItemType | PhotoItemType) {
  if (item.mediaType === MediaEnum.Photo) {
    return !item.uploaded;
  }

  if (isVideoItem(item)) {
    return item.uploaded < item.fileLength;
  }

  return false;
}

export function isItemOriginal(item: PhotoItemType) {
  return item.originalPhotoId;
}
